import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  BarElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import { hexToRgb } from 'common/styles/colors';

ChartJS.register(LineElement, PointElement, BarElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

const OverviewLineChart = ({ lineChartData, maxYAxis }) => {
  const recordLabel = useMemo(() => {
    if (!Array.isArray(lineChartData)) {
      return '';
    }

    for (const dataSet of lineChartData) {
      for (const item of dataSet.data || []) {
        if (item?.option_name === 'Outcome Score' && item?.option_label) {
          return item.option_label;
        }
      }
    }

    return '';
  }, [lineChartData]);

  const chartData = useMemo(() => {
    if (!Array.isArray(lineChartData) || lineChartData.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const groupedData = {
      'Outcome Score': [],
      'Overall Score': [],
    };
    const optionLabels = [];

    lineChartData.forEach(dataSet => {
      optionLabels.push(dataSet.name);

      (dataSet.data || []).forEach(item => {
        const value = item?.overall_score;
        let parsedValue = null;

        if (value !== null && value !== undefined) {
          if (typeof value === 'number') {
            parsedValue = Number(value.toFixed?.(1) ?? value);
          } else {
            const normalizedValue = String(value).replace(/,/g, '');
            parsedValue = Number(normalizedValue);
          }

          if (!Number.isFinite(parsedValue)) {
            parsedValue = null;
          }
        }

        if (item?.option_name === 'Outcome Score') {
          groupedData['Outcome Score'].push(parsedValue);
        }

        if (item?.option_name === 'Overall Score') {
          groupedData['Overall Score'].push(parsedValue);
        }
      });
    });

    return {
      labels: optionLabels,
      datasets: [
        {
          type: 'bar',
          label: recordLabel || 'Outcome Score',
          data: groupedData['Outcome Score'],
          backgroundColor: 'rgba(165, 207, 238, 0.8)',
          borderColor: 'rgba(165, 207, 238, 1.0)',
          borderWidth: 1,
          yAxisID: 'y1',
          order: 2,
        },
        {
          type: 'line',
          label: 'Culture Score',
          data: groupedData['Overall Score'],
          borderColor: hexToRgb('#2257FF', 0.8),
          backgroundColor: hexToRgb('#2257FF', 0.2),
          fill: false,
          tension: 0.3,
          pointBackgroundColor: hexToRgb('#2257FF', 1.0),
          pointBorderColor: '#aaa',
          pointHoverBorderColor: '#000',
          pointRadius: 5,
          pointHoverRadius: 8,
          yAxisID: 'y2',
          order: 1,
        },
      ],
    };
  }, [lineChartData, recordLabel]);

  const options = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            font: {
              family: 'Poppins',
              size: 12,
            },
          },
        },
        tooltip: {
          bodyFont: {
            family: 'Poppins',
            size: 12,
          },
          titleFont: {
            family: 'Poppins',
            size: 14,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: false,
          },
          ticks: {
            font: {
              family: 'Poppins',
              size: 12,
            },
          },
        },
        y1: {
          type: 'linear',
          position: 'left',
          min: 0,
          max: maxYAxis,
          title: {
            display: true,
            text: recordLabel || 'Outcome Score',
            font: {
              family: 'Poppins',
              size: 12,
            },
          },
          ticks: {
            font: {
              family: 'Poppins',
              size: 12,
            },
          },
        },
        y2: {
          type: 'linear',
          position: 'right',
          min: 0,
          max: 10,
          title: {
            display: true,
            text: 'Culture Score',
            font: {
              family: 'Poppins',
              size: 12,
            },
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            font: {
              family: 'Poppins',
              size: 12,
            },
          },
        },
      },
    }),
    [maxYAxis, recordLabel]
  );

  return <Bar data={chartData} options={options} />;
};

export default OverviewLineChart;
